/**
 * Conversation Speech Module
 *
 * Handles free-form speech recognition for conversation practice.
 * Uses pronunciation assessment with empty reference text (unscripted mode)
 * to get real pronunciation scores without requiring a reference.
 *
 * Platform implementation:
 * - iOS/Android: NativeAzureSpeech (TurboModule) — on-device Azure Speech SDK
 *   pointed at the backend WS speech relay via `host` + Supabase JWT (see
 *   ConversationParams).
 * - Web: not supported. The UI gates voice off via SPEECH_RECOGNITION_AVAILABLE
 *   (config/speechCapabilities.ts), so this module is normally unreachable on
 *   web; if reached anyway, the NativeAzureSpeech.web.ts stub rejects with
 *   `not_supported` as defense-in-depth.
 */

import { Platform } from 'react-native';
import type { AssessmentConfig, PronunciationResult } from './NativeAzureSpeech';
import NativeAzureSpeech from './NativeAzureSpeech';
import { waitForRecognizerIdle } from './recognizerIdle';
import { nativeErrorCode } from './nativeErrorCode';
import { consumeE2ESpeechResult } from './e2eSpeechInjection';
import { consumeInjectedAudio } from './e2eAudioInjection';
import type { E2ESpeechInjection } from './e2eSpeechInjection';
import { speechEventEmitter } from './speechEventEmitter';
import { webEventEmitter } from './webEventEmitter';
import { ASSESSMENT_DEFAULTS, SCRIPTED_INITIAL_SILENCE_MS } from './speechDefaults';
import { selectProfile, getProfileConfig } from './assessmentProfiles';
import type { PronunciationFeatures } from './assessmentProfiles';
import { createLogger } from '../utils/logger';
import { SpeechLogger } from './AzureSpeechLogger';

// Logger instance — recreated per session with config flags
let slog = new SpeechLogger(createLogger('ConversationSpeech'), false, false);

/**
 * Conversation recording parameters
 */
export interface ConversationParams {
  /** Relay WS origin for `SpeechConfig.fromHost()` (server-speech-proxy Phase 2). */
  host: string;
  /** Supabase session JWT — the SDK's authorizationToken (Bearer on the WS upgrade). */
  token: string;
  language: string;
  enablePronunciationAssessment?: boolean;
  maxDurationMs?: number;
  phraseList?: string[]; // Words/phrases to boost recognition (e.g., app name, user name)
  referenceText?: string; // When set, uses scripted assessment mode (pronunciation_assessment directive)
  pronunciationFeatures?: PronunciationFeatures | null; // Per-language feature flags from locale data
  /** Chat session to bill this recording's speech cost to — see `AssessmentConfig.sessionId`. */
  sessionId?: string;
  /** Checked after the recognizer-idle wait, right before the native session
   *  starts. A cancel that lands during that wait would otherwise spawn a
   *  session nobody owns (hot mic, blocked retries). Return true to abort. */
  shouldAbort?: () => boolean;
}

/**
 * Conversation result (extends PronunciationResult with additional fields)
 */
export interface ConversationResult {
  transcription: string;
  pronunciationResult?: PronunciationResult;
  durationMs: number;
  userCanceled?: boolean;
  autoStopped?: boolean;
}

/**
 * Event callbacks for conversation recording
 */
export interface ConversationEventCallbacks {
  onRecognizing?: (text: string) => void;
  onCompleted?: (result: ConversationResult) => void;
  /** `code` is the native reject code (e.g. 'no_match_initial_silence_timeout',
   *  'canceled', 'already_recognizing') — lets the UI pick a specific message. */
  onError?: (error: string, code?: string) => void;
  onCanceled?: (reason: string) => void;
  onSilenceWarning?: (secondsUntilEnd: number) => void;
  onSilenceCleared?: () => void;
}

/**
 * Event subscription handle
 */
export interface EventSubscription {
  remove: () => void;
}

// Current state
let isCurrentlyRecognizing = false;
let eventCallbacks: ConversationEventCallbacks = {};
let recordingStartTime: number = 0;

/**
 * Conversation Speech Module
 */
class ConversationSpeechModule {
  /**
   * Start conversation recording (free-form speech with pronunciation scoring)
   *
   * Uses NativeAzureSpeech.startPronunciationAssessment with empty reference
   * text (unscripted mode). iOS/Android only — on web the UI gate
   * (SPEECH_RECOGNITION_AVAILABLE) makes this unreachable, and the
   * NativeAzureSpeech.web.ts stub rejects with `not_supported` if reached.
   */
  async startConversationRecording(
    params: ConversationParams,
  ): Promise<ConversationResult> {
    // Capture the callbacks registered for THIS session. If the consumer
    // re-subscribes (new recording) or unsubscribes (cancel) before this
    // session's promise settles, its completion/error must not fire the new
    // session's callbacks — that could send a take the user cancelled.
    const sessionCallbacks = eventCallbacks;
    const callbacksAreCurrent = () => eventCallbacks === sessionCallbacks;

    // Subscribe to recognizing events for real-time partial transcription
    const emitter = Platform.OS === 'web' ? webEventEmitter : speechEventEmitter!;

    const subscription = emitter.addListener(
      'onRecognizing',
      (event: { text?: string }) => {
        if (event.text && callbacksAreCurrent() && sessionCallbacks.onRecognizing) {
          sessionCallbacks.onRecognizing(event.text);
        }
      },
    );

    try {
      isCurrentlyRecognizing = true;
      recordingStartTime = Date.now();

      // A just-cancelled session may still be tearing down — wait for the
      // recognizer to go idle so an immediate retry doesn't reject with
      // already_recognizing.
      await waitForRecognizerIdle();

      if (params.shouldAbort?.()) {
        isCurrentlyRecognizing = false;
        return { transcription: '', durationMs: 0, userCanceled: true };
      }

      // E2E (dev builds only): a queued canned outcome replaces the native
      // recognizer — the sim can't feed real mic audio. No-op in production.
      const injected = consumeE2ESpeechResult();
      if (injected) {
        return await this.runInjectedRecording(
          injected,
          params,
          sessionCallbacks,
          callbacksAreCurrent,
        );
      }

      const language = params.language;

      // Select assessment profile from reference text length
      const hasReference = !!params.referenceText?.trim();
      const profile = hasReference
        ? selectProfile(params.referenceText)
        : 'free';
      const profileOverrides = getProfileConfig(profile, params.pronunciationFeatures);

      // Build config from shared defaults + profile overrides + session overrides
      const config: AssessmentConfig = {
        ...ASSESSMENT_DEFAULTS,
        ...profileOverrides,
        language,
        referenceText: hasReference ? params.referenceText! : '',
        endOfTurnSilenceMs: hasReference ? 3000 : 5000, // Shorter for scripted — user says the phrase and stops
        // Scripted: give the user time to read the reference before speaking
        ...(hasReference ? { initialSilenceTimeoutMs: SCRIPTED_INITIAL_SILENCE_MS } : {}),
        phraseList: params.phraseList,
        sessionId: params.sessionId,
      };

      // E2E audio injection (dev builds only): unlike `consumeE2ESpeechResult`
      // above, this does NOT bypass the recognizer — it swaps only the audio
      // source, so the take still runs through the real native recognizer, relay
      // and Azure assessment. Consume-once and E2E_MODE-gated; no-op in
      // production. See e2eAudioInjection.ts.
      const injectedAudio = consumeInjectedAudio();
      if (injectedAudio) {
        config.injectedAudioBase64 = injectedAudio.wavBase64;
      }

      // Recreate logger with config flags for this session
      slog = new SpeechLogger(
        createLogger('ConversationSpeech'),
        config.logRN ?? __DEV__,
        config.logSDK ?? __DEV__,
      );

      slog.rnEvent('Profile', 'start', {
        profile,
        referenceText: params.referenceText ?? '(none)',
      });

      if (injectedAudio) {
        slog.rnEvent('AudioInjected', 'start', {
          clipId: injectedAudio.clipId,
          sha256: injectedAudio.sha256,
        });
      }

      // All profiles run on the continuous recognizer — it has a safe, prompt
      // stop/cancel and fully supports scripted assessment (single-segment takes
      // short-circuit through mergeSegments).
      const result = await NativeAzureSpeech.startPronunciationAssessment(
        params.host,
        params.token,
        config,
      );

      isCurrentlyRecognizing = false;

      const durationMs = Date.now() - recordingStartTime;

      slog.rnEvent('Result', 'end', {
        recognizedText: result.recognizedText,
        accuracyScore: result.accuracyScore,
        pronunciationScore: result.pronunciationScore,
        wordCount: result.words?.length ?? 0,
        duration: result.duration,
      });

      const conversationResult: ConversationResult = {
        // Chat UI shows Azure's Display form (punctuated/cased). recognizedText
        // stays lexical for the assessment/LLM-comparison path.
        transcription: result.displayText,
        durationMs: result.duration || durationMs,
        pronunciationResult:
          params.enablePronunciationAssessment !== false &&
          result.recognizedText.length > 0
            ? { ...result, profile }
            : undefined,
        userCanceled: result.userCanceled ?? false,
        autoStopped: result.autoStopped ?? false,
      };

      if (callbacksAreCurrent() && sessionCallbacks.onCompleted) {
        sessionCallbacks.onCompleted(conversationResult);
      }

      return conversationResult;
    } catch (error: unknown) {
      isCurrentlyRecognizing = false;
      const nativeCode = nativeErrorCode(error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to start conversation recording';

      slog.rnAnomaly('RecordingFailed', { code: nativeCode, message: errorMessage });

      if (callbacksAreCurrent() && sessionCallbacks.onError) {
        sessionCallbacks.onError(errorMessage, nativeCode);
      }

      throw error instanceof Error
        ? error
        : new Error('Failed to start conversation recording');
    } finally {
      subscription.remove();
    }
  }

  /**
   * E2E-only: resolve the recording from a queued canned outcome instead of
   * the native recognizer. Mirrors the real completion paths so the JS state
   * machine (VoiceRecorder / ConversationStore) behaves identically:
   * fires `onRecognizing` (live transcription), then `onCompleted`/`onError`.
   * The `callbacksAreCurrent` guard makes a cancel during the hold window
   * suppress completion, exactly as a real session would.
   */
  private async runInjectedRecording(
    injection: E2ESpeechInjection,
    params: ConversationParams,
    sessionCallbacks: ConversationEventCallbacks,
    callbacksAreCurrent: () => boolean,
  ): Promise<ConversationResult> {
    slog.rnEvent('E2EInjection', 'start', { kind: injection.kind });
    // Hold the "recording" state briefly so the overlay is observable.
    await new Promise((resolve) => setTimeout(resolve, injection.delayMs ?? 600));
    isCurrentlyRecognizing = false;
    const durationMs = Date.now() - recordingStartTime;

    if (injection.kind === 'error') {
      if (callbacksAreCurrent() && sessionCallbacks.onError) {
        sessionCallbacks.onError(injection.message, injection.code);
      }
      throw new Error(injection.message);
    }

    if (injection.kind === 'no-speech') {
      // Empty transcription drives VoiceRecorder's "no speech detected" branch.
      const emptyResult: ConversationResult = { transcription: '', durationMs, userCanceled: false };
      if (callbacksAreCurrent() && sessionCallbacks.onCompleted) {
        sessionCallbacks.onCompleted(emptyResult);
      }
      return emptyResult;
    }

    // kind === 'result': surface a live partial, then complete with scores.
    if (callbacksAreCurrent() && sessionCallbacks.onRecognizing) {
      sessionCallbacks.onRecognizing(injection.transcription);
    }

    const pronunciationResult: PronunciationResult | undefined =
      params.enablePronunciationAssessment === false
        ? undefined
        : {
            recognizedText: injection.transcription,
            displayText: injection.transcription,
            accuracyScore: injection.scores.accuracy,
            fluencyScore: injection.scores.fluency,
            completenessScore: injection.scores.completeness,
            pronunciationScore: injection.scores.pronunciation,
            words: [],
            duration: durationMs,
          };

    const result: ConversationResult = {
      transcription: injection.transcription,
      durationMs,
      pronunciationResult,
      userCanceled: false,
    };
    if (callbacksAreCurrent() && sessionCallbacks.onCompleted) {
      sessionCallbacks.onCompleted(result);
    }
    return result;
  }

  /**
   * Stop ongoing recording (graceful — the take is kept).
   * The continuous session stops and resolves with the merged segments.
   */
  stopRecording(): void {
    try {
      NativeAzureSpeech.stopRecognition();
      isCurrentlyRecognizing = false;
    } catch (error) {
      slog.rnAnomaly('StopRecordingError', {
        error: error instanceof Error ? error.message : String(error),
      });
      isCurrentlyRecognizing = false;
    }
  }

  /**
   * Cancel ongoing recording (the take is discarded).
   * Stops the continuous session and releases the microphone — use for the
   * cancel button; stopRecording for the stop/send button.
   */
  cancelRecording(): void {
    try {
      NativeAzureSpeech.cancelRecognition();
      isCurrentlyRecognizing = false;
    } catch (error) {
      slog.rnAnomaly('CancelRecordingError', {
        error: error instanceof Error ? error.message : String(error),
      });
      // Native builds predating cancelRecognition: fall back to stop.
      this.stopRecording();
    }
  }

  /**
   * Check if currently recording
   */
  isRecording(): boolean {
    return isCurrentlyRecognizing;
  }

  /**
   * Subscribe to recording events
   */
  subscribeToEvents(callbacks: ConversationEventCallbacks): EventSubscription {
    eventCallbacks = callbacks;

    return {
      remove: () => {
        // Only clear if this subscription is still the active one — a stale
        // handle's late remove() must not wipe a newer session's callbacks.
        if (eventCallbacks === callbacks) {
          eventCallbacks = {};
        }
      },
    };
  }

  /**
   * Remove all event listeners
   */
  removeAllListeners(): void {
    eventCallbacks = {};
  }
}

/**
 * Singleton instance
 */
export const ConversationSpeech = new ConversationSpeechModule();
